import React, { useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, Clock, PhoneCall, Eye, CalendarClock, Phone, RotateCcw, DollarSign, MapPin, Trophy, StickyNote } from 'lucide-react';
import { ClaimedLoan, pointsForFunding } from './types';
import { CallOutcomeBadge } from './CallOutcomeBadge';
interface MyClaimsProps {
  claims: ClaimedLoan[];
  onRelease: (id: string) => void;
  onLogCall: (loan: ClaimedLoan) => void;
  onViewLoan: (loan: ClaimedLoan) => void;
  onViewTimeline: (loan: ClaimedLoan) => void;
  onViewNotes: (loan: ClaimedLoan) => void;
  onDial: (loan: ClaimedLoan) => void;
  onJumpToTank: () => void;
}
type StatusFilter = 'all' | 'pending' | 'funded';
export function MyClaims({
  claims,
  onRelease,
  onLogCall,
  onViewLoan,
  onViewTimeline,
  onViewNotes,
  onDial,
  onJumpToTank
}: MyClaimsProps) {
  const [filter, setFilter] = useState<StatusFilter>('all');
  const filtered = claims.filter(c => filter === 'all' ? true : c.status === filter);
  const counts = {
    all: claims.length,
    pending: claims.filter(c => c.status === 'pending').length,
    funded: claims.filter(c => c.status === 'funded').length
  };
  return <div className="space-y-5" data-id="element-358">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-id="element-359">
        <div className="flex items-center gap-2" data-id="element-360">
          {(['all', 'pending', 'funded'] as StatusFilter[]).map(f => <button key={f} onClick={() => setFilter(f)} className={`px-4 py-1.5 rounded-full text-sm font-medium capitalize transition-all ${filter === f ? 'bg-orange-500/20 text-orange-400 border border-orange-500/30' : 'bg-slate-800/50 text-slate-400 border border-slate-700/50 hover:bg-slate-800 hover:text-slate-200'}`} data-id="element-361">
              {f} <span className="ml-1 text-xs opacity-70" data-id="element-362">({counts[f]})</span>
            </button>)}
        </div>
        <button onClick={onJumpToTank} className="flex items-center justify-center gap-2 px-5 py-2 rounded-lg bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-400 hover:to-orange-400 text-white text-sm font-bold shadow-lg shadow-red-500/20 transition-all hover:-translate-y-0.5" data-id="element-363">
          Jump into the Tank →
        </button>
      </div>

      {filtered.length === 0 ? <EmptyState onJumpToTank={onJumpToTank} filter={filter} data-id="element-364" /> : <div className="space-y-4" data-id="element-365">
          <AnimatePresence data-id="element-366">
            {filtered.map((claim, idx) => {
          const latestNote = claim.notes[claim.notes.length - 1];
          return <motion.div key={claim.id} layout="position" transition={{
            duration: 0.15
          }} className={`relative overflow-hidden rounded-xl border p-5 ${claim.status === 'funded' ? 'border-emerald-500/40 bg-gradient-to-br from-emerald-500/10 to-slate-900' : 'border-slate-700 bg-gradient-to-br from-slate-800/60 to-slate-900'}`} data-id="element-367">
                  <div className="flex flex-col lg:flex-row gap-6 items-start lg:items-center justify-between" data-id="element-368">
                    <div className="flex-1 min-w-0" data-id="element-369">
                      <div className="flex items-center flex-wrap gap-2 mb-2" data-id="element-370">
                        <h3 className="text-xl font-bold font-display text-white truncate mr-1" data-id="element-371">
                          {claim.borrowerName}
                        </h3>
                        <StatusBadge status={claim.status} data-id="element-372" />
                        <CallOutcomeBadge outcome={claim.lastCallOutcome} when={claim.lastCallAt} data-id="element-373" />
                        {claim.status === 'pending' && <span className="text-[10px] font-semibold uppercase tracking-wider text-orange-300 bg-orange-500/10 border border-orange-500/20 px-2 py-1 rounded" data-id="element-374">
                            +{pointsForFunding(claim.amount)} pts on funding
                          </span>}
                      </div>

                      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-slate-300" data-id="element-375">
                        <div className="flex items-center gap-1.5" data-id="element-376">
                          <DollarSign className="w-4 h-4 text-emerald-400" data-id="element-377" />
                          <span className="font-semibold text-white" data-id="element-378">
                            ${claim.amount.toLocaleString()}
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5" data-id="element-379">
                          <MapPin className="w-4 h-4 text-slate-400" data-id="element-380" />
                          <span data-id="element-381">{claim.state}</span>
                        </div>
                        <div className="flex items-center gap-1.5" data-id="element-382">
                          <span className="w-2 h-2 rounded-full bg-slate-500" data-id="element-383" />
                          <span data-id="element-384">{claim.stage}</span>
                        </div>
                        <div className="flex items-center gap-1.5 text-slate-400" data-id="element-385">
                          <Clock className="w-3.5 h-3.5" data-id="element-386" />
                          <span data-id="element-387">Claimed {formatRelative(claim.claimedAt)}</span>
                        </div>
                      </div>

                      {latestNote && <button onClick={() => onViewNotes(claim)} className="mt-3 flex items-start gap-2 text-left rounded-lg border border-slate-800 bg-slate-950/40 px-3 py-2 hover:border-slate-700 hover:bg-slate-950/70 transition-colors w-full max-w-2xl group/note" data-id="element-388">
                          <StickyNote className="w-3.5 h-3.5 text-yellow-400/80 shrink-0 mt-0.5" data-id="element-389" />
                          <div className="min-w-0 flex-1" data-id="element-390">
                            <p className="text-[11px] text-slate-500" data-id="element-391">
                              <span className="font-semibold text-slate-400" data-id="element-392">
                                {latestNote.author}
                              </span>
                              {' · '}
                              {new Date(latestNote.timestamp).toLocaleString()}
                              {claim.notes.length > 1 && <span className="ml-2 text-slate-500" data-id="element-393">
                                  +{claim.notes.length - 1} more
                                </span>}
                            </p>
                            <p className="text-xs text-slate-300 line-clamp-1 italic group-hover/note:text-slate-200" data-id="element-394">
                              “{latestNote.body}”
                            </p>
                          </div>
                        </button>}
                    </div>

                    <div className="flex flex-col gap-2 w-full lg:w-auto" data-id="element-395">
                      <div className="flex items-center gap-2 justify-end flex-wrap" data-id="element-396">
                        <IconButton title="View Loan" onClick={() => onViewLoan(claim)} icon={Eye} data-id="element-397" />
                        <IconButton title="View Timeline" onClick={() => onViewTimeline(claim)} icon={CalendarClock} data-id="element-398" />
                        <IconButton title={`Notes (${claim.notes.length})`} onClick={() => onViewNotes(claim)} icon={StickyNote} badge={claim.notes.length || undefined} data-id="element-399" />
                        <IconButton title="Log Call Outcome" onClick={() => onLogCall(claim)} icon={PhoneCall} data-id="element-400" />
                        <IconButton title="Click to Dial" onClick={() => onDial(claim)} icon={Phone} accent data-id="element-401" />
                        <IconButton title="Release back to Tank" onClick={() => onRelease(claim.id)} icon={RotateCcw} data-id="element-402" />
                      </div>
                      {claim.status === 'funded' && <div className="flex items-center justify-center gap-2 px-6 py-2.5 rounded-lg bg-emerald-500/15 text-emerald-300 text-sm font-bold border border-emerald-500/30" data-id="element-403">
                          <Trophy className="w-4 h-4" data-id="element-404" />
                          Funded — +{pointsForFunding(claim.amount)} pts
                        </div>}
                    </div>
                  </div>
                </motion.div>;
        })}
          </AnimatePresence>
        </div>}
    </div>;
}
function StatusBadge({
  status
}: {
  status: 'pending' | 'funded';
}) {
  if (status === 'funded') {
    return <span className="flex items-center gap-1 px-2 py-1 rounded bg-emerald-500/20 text-emerald-400 text-[10px] font-bold uppercase tracking-wider border border-emerald-500/30" data-id="element-405">
        <CheckCircle2 className="w-3 h-3" data-id="element-406" /> Funded
      </span>;
  }
  return <span className="flex items-center gap-1 px-2 py-1 rounded bg-orange-500/20 text-orange-400 text-[10px] font-bold uppercase tracking-wider border border-orange-500/20" data-id="element-407">
      <Clock className="w-3 h-3" data-id="element-408" /> Pending
    </span>;
}
function IconButton({
  title,
  onClick,
  icon: Icon,
  accent,
  badge
}: {
  title: string;
  onClick: () => void;
  icon: ComponentType<{
    className?: string;
  }>;
  accent?: boolean;
  badge?: number;
}) {
  return <button onClick={onClick} title={title} aria-label={title} className={`relative p-2 rounded-lg border transition-colors ${accent ? 'bg-blue-500/15 border-blue-500/30 text-blue-300 hover:bg-blue-500/25' : 'bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700 hover:text-white'}`} data-id="element-409">
      <Icon className="w-4 h-4" data-id="element-410" />
      {badge ? <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 rounded-full bg-orange-500 text-white text-[9px] font-bold flex items-center justify-center" data-id="element-411">
          {badge}
        </span> : null}
    </button>;
}
function EmptyState({
  onJumpToTank,
  filter
}: {
  onJumpToTank: () => void;
  filter: StatusFilter;
}) {
  const messages = {
    all: {
      title: 'No claimed loans yet',
      copy: 'Jump into the Shark Tank to claim your first lead.'
    },
    pending: {
      title: 'No pending claims',
      copy: 'Everything you’ve claimed has been funded — go grab more.'
    },
    funded: {
      title: 'No funded loans yet',
      copy: 'Close out a pending claim to start racking up points.'
    }
  };
  const m = messages[filter];
  return <div className="text-center py-16 rounded-2xl border border-dashed border-slate-800 bg-slate-900/40" data-id="element-412">
      <h3 className="text-lg font-bold font-display text-white mb-1" data-id="element-413">
        {m.title}
      </h3>
      <p className="text-sm text-slate-400 mb-5" data-id="element-414">{m.copy}</p>
      <button onClick={onJumpToTank} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-gradient-to-r from-red-500 to-orange-500 text-white font-bold text-sm shadow-lg shadow-red-500/20 hover:-translate-y-0.5 transition-all" data-id="element-415">
        Jump into the Tank →
      </button>
    </div>;
}
function formatRelative(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const hours = Math.floor(diff / (1000 * 60 * 60));
  if (hours < 1) return 'just now';
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}